import Icon from '@/components/snippets/icon/icon'
import clsx from 'clsx'
import { useAthosContext } from '../fetchers/provider'
import { useEffect, useMemo, useState } from 'preact/hooks'
import { FilterList } from './FilterItem'
import { useOption } from '../hooks/useOption'
import { FilterSelected } from './FilterSelected'
import { parseFiltersFromResults, mergeFilterSnapshots } from './FilterUtils'
import { useFilters } from '../hooks/useFilters'
import { calculateProductCount } from '../helpers/productCount'

export function FilterDrawer({
  buttonClass = 'btn btn-text-black btn-md btn-inline underline',
  showToggle = true,
  nested = false,
  stickyFilters
}) {
  const { results, refine, isLoading } = useAthosContext()
  const { handleToggleFilter, handleRemoveFilter, handleClearFilters, handleRemoveAllByField } = useFilters()

  const activeFilters = useOption('filters', {
    defaultValue: new Set(),
    syncWithURL: true
  })

  const { fetchedFilters, sizeFilters, displayOrder } = useMemo(() => parseFiltersFromResults(results), [results])
  const productCount = calculateProductCount(results)

  const [initialFilters, setInitialFilters] = useState([])
  const [initialSizeFilters, setInitialSizeFilters] = useState([])
  const [initialDisplayOrder, setInitialDisplayOrder] = useState([])
  const [restorableFilters, setRestorableFilters] = useState(new Set())

  useEffect(() => {
    const filtersCleared = !activeFilters || activeFilters.size === 0
    setInitialFilters((prev) => mergeFilterSnapshots(prev, fetchedFilters, filtersCleared))
    setInitialSizeFilters((prev) => mergeFilterSnapshots(prev, sizeFilters, filtersCleared))
    if (displayOrder.length > 0 && initialDisplayOrder.length === 0) {
      setInitialDisplayOrder(displayOrder)
    } else if (
      initialDisplayOrder.length > 0 &&
      !initialDisplayOrder.some((item) => item.type === 'size') &&
      displayOrder.some((item) => item.type === 'size')
    ) {
      setInitialDisplayOrder(displayOrder)
    }
  }, [fetchedFilters, sizeFilters, displayOrder, activeFilters])

  const handleOpenFilters = () => {
    if (initialFilters.length === 0) setInitialFilters(fetchedFilters)
    if (initialSizeFilters.length === 0) setInitialSizeFilters(sizeFilters)
    if (initialDisplayOrder.length === 0) setInitialDisplayOrder(displayOrder)
    setRestorableFilters(new Set(activeFilters))
  }

  useEffect(() => {
    const toggles = document.querySelectorAll('[data-modal-parent="filters"][data-modal-role="toggle"]')
    toggles.forEach((el) => el.addEventListener('click', handleOpenFilters))
    return () => toggles.forEach((el) => el.removeEventListener('click', handleOpenFilters))
  })

  const handleCancelFilters = () => {
    refine('filters', restorableFilters)
    setRestorableFilters(new Set())
  }

  const filtersBanner =
    typeof window !== 'undefined' && window.collection?.filters?.banner_image
      ? (() => {
          const bannerUrl = window.collection.filters.banner_url
          const bannerImage = window.collection.filters.banner_image
          const bannerMobileImage = window.collection.filters.banner_mobile_image
          const className = `shrink-0 w-full h-fit my-6 md-max:px-4 overflow-hidden ${bannerMobileImage ? 'mob-alt_lg' : ''}`
          const imageContent = (
            <>
              <img src={bannerImage?.src || bannerImage} alt="" className="w-full h-fit" />
              {bannerMobileImage && (
                <img src={bannerMobileImage?.src || bannerMobileImage} alt="" className="w-full h-fit" />
              )}
            </>
          )

          return bannerUrl ? (
            <a href={bannerUrl} aria-label="Filters banner link" className={className}>
              {imageContent}
            </a>
          ) : (
            <div className={className}>{imageContent}</div>
          )
        })()
      : null

  const content = (
    <>
      {showToggle && (
        <button
          className={buttonClass}
          data-modal-role="toggle"
          data-modal-parent="filters"
          onClick={handleOpenFilters}
        >
          <Icon id="filter" className="w-4 h-4" /> Filter {activeFilters?.size > 0 && <>({activeFilters.size})</>}
        </button>
      )}

      <FixedFilterBarMobile
        activeFilters={activeFilters}
        handleOpenFilters={handleOpenFilters}
        enabled={stickyFilters !== true}
        revealOnScroll={stickyFilters === false}
      />

      <button
        data-modal-role="close"
        data-modal-parent="filters"
        className="pointer-events-none fixed inset-0 z-max bg-black/60 opacity-0 transition-all group-data-visible/filters:pointer-events-auto group-data-visible/filters:opacity-100 md:hidden"
        aria-label="Close filters"
      />
      <div
        className={clsx(
          'pointer-events-none fixed inset-6 z-max flex flex-col overflow-hidden md-max:rounded-lg bg-primary-white opacity-0 transition-all',
          'group-data-visible/filters:pointer-events-auto group-data-visible/filters:opacity-100',
          'md:sticky md:inset-auto md:z-auto md:w-0 md:self-start md:shrink-0 md:rounded-none md:bg-transparent md:opacity-100 md:overflow-hidden',
          'md:group-data-visible/filters:w-80',
          stickyFilters !== false
            ? 'md:top-48 md:max-h-[calc(100dvh-7rem)]'
            : 'md:top-[calc(60px+2.5rem)] xl:top-[calc(82px+2.5rem)] md:max-h-[calc(100dvh-60px-2.5rem)] xl:max-h-[calc(100dvh-82px-2.5rem)]'
        )}
      >
        <div
          className="
            relative flex h-full w-full flex-col overflow-hidden no-scrollbar bg-primary-white transition-all
            -translate-x-4 group-data-visible/filters:translate-x-0
            md:w-80 md:-translate-x-full md:group-data-visible/filters:translate-x-0
          "
        >
          <div className="flex min-h-0 grow flex-col md:pl-10">
            <div className="flex shrink-0 justify-between bg-primary-white px-4 md:px-0 pr-1 md-max:pt-2 pb-2 md:pb-4">
              <p className="my-auto h4">Filters</p>
              <button
                data-modal-role="close"
                data-modal-parent="filters"
                className="btn btn-md btn-icon btn-inline md:hidden"
                aria-label="Close filters"
              >
                <Icon id="close" />
              </button>
            </div>
            <div className="shrink-0 md:hidden">
              <FilterSelected activeFilters={activeFilters} onRemove={handleRemoveFilter} onClear={handleClearFilters} />
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll no-scrollbar">
              <FilterList
                displayOrder={initialDisplayOrder}
                initialFilters={initialFilters}
                initialSizeFilters={initialSizeFilters}
                fetchedFilters={fetchedFilters}
                sizeFilters={sizeFilters}
                activeFilters={activeFilters}
                onFilter={handleToggleFilter}
                removeAll={handleRemoveAllByField}
              />
            </div>
          </div>
          {filtersBanner}
          <div className="flex shrink-0 gap-4 bg-neutrals-base p-4 md:hidden">
            <button
              data-modal-role="close"
              data-modal-parent="filters"
              className="btn btn-outline-black btn-md"
              onClick={handleCancelFilters}
            >
              Cancel
            </button>
            <button
              data-modal-role="close"
              data-modal-parent="filters"
              className="btn btn-solid-black btn-md flex-grow justify-center"
            >
              {activeFilters.size > 0 && productCount > 0 && !isLoading
                ? `View ${productCount} Results`
                : 'Apply Filters'}
            </button>
          </div>
        </div>
      </div>
    </>
  )

  if (nested) return content

  return (
    <x-modal className="group/filters" data-id="filters">
      {content}
    </x-modal>
  )
}

const FixedFilterBarMobile = ({ activeFilters, handleOpenFilters, enabled = true, revealOnScroll = false }) => {
  const [revealed, setRevealed] = useState(!revealOnScroll)

  useEffect(() => {
    if (!enabled || !revealOnScroll) return

    const onScroll = () => {
      setRevealed(window.scrollY > window.innerHeight / 3 * 2)
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [enabled, revealOnScroll])

  if (!enabled) return null

  return (
    <div
      className={`flex md:hidden items-stretch fixed bottom-4 right-4 rounded-full bg-primary-black overflow-hidden transition-all z-30 ${
        revealed ? 'translate-x-0 opacity-100' : 'pointer-events-none translate-x-full opacity-0'
      }`}
    >
      <button
        className="btn btn-solid-black btn-md btn-inline px-6"
        data-modal-role="toggle"
        data-modal-parent="filters"
        onClick={handleOpenFilters}
      >
        <Icon id="filter" className="h-4 w-4" />
        Filter {activeFilters?.size > 0 && <>({activeFilters.size})</>}
      </button>
      <span className="block h-auto border-r border-r-primary-white" />
      <button
        className="btn btn-solid-black btn-md btn-inline px-6"
        type="button"
        aria-label="Sort By"
        onClick={() => document.querySelector('x-modal[data-id="sort"]')?.handleToggle()}
      >
        <Icon id="arrow-up-down" className="h-4 w-4" />
        Sort By
      </button>
    </div>
  )
}
